import { Popup } from '@progress/kendo-react-popup'
import {
    SchedulerItem,
    SchedulerItemContent,
    SchedulerItemHandle,
    SchedulerItemProps,
} from '@progress/kendo-react-scheduler'
import { Fragment, useEffect, useRef, useState } from 'react'
import { default_primary_color } from '../../../../../context/AuthContext'
import { useTenantContext } from '../../../../../context/TenantContext'
import { addAlpha, classNames } from '../../../../../utils/dom-helpers'
import ConfirmDialogModal from '../../../ConfirmDialog'
import { getTranslation } from '../../../../../utils/language-helper'
import { useAppContext } from '../../../../../context/AppContext'

export type CustomItemProps = {
    onDelete(item_id: string): void
}

export const CustomItem = (props: SchedulerItemProps & CustomItemProps) => {
    const ref = useRef<SchedulerItemHandle>(null)
    const [schedule_info, setScheduleInfo] = useState<any>()
    const { primaryColor } = useTenantContext()
    const { translations } = useAppContext()
    const icon_ref = useRef<HTMLInputElement>(null)
    const [show_popup, setShowPopup] = useState(false)
    const [show_more, setShowMore] = useState(false)
    const [showConfirmation, setShowConfirmation] = useState(false)

    useEffect(() => {
        if (props.dataItem) {
            let recurrence_days: string[] = []
            props.dataItem.dayOfWeeks?.forEach((day: string) => {
                recurrence_days.push(day.toLowerCase().substring(0, 3))
            })
    
            let service_list: string[] = []
            if (props.dataItem.services) {
                props.dataItem.services.forEach((svc: any) => {
                    service_list.push(svc.serviceName)
                })
            } else {
                props.dataItem.categories.forEach((category: any) => {
                    service_list.push(category.categoryName)
                })
            }
    
            let service_display = service_list.slice(0, 2)
            let service_more = service_list.slice(2, service_list.length)
    
            setScheduleInfo({
                ...schedule_info,
                days: recurrence_days.join(', '),
                start: props.dataItem.start.toLocaleString(undefined, {
                    hour12: true,
                    minute: '2-digit',
                    hour: 'numeric',
                }),
                end: props.dataItem.end.toLocaleString(undefined, {
                    hour12: true,
                    minute: '2-digit',
                    hour: 'numeric',
                }),
                svc_display: service_display,
                svc_more: service_more
            })
    
            const element = ref.current && ref.current.element
    
            if (!element) return
            const primary_hex = props.dataItem.block ? '#dc2626' : '#15803d'

            element.style.backgroundColor = addAlpha(primary_hex, 0.2)
            element.style.borderLeft = `4px solid ${primary_hex}`
            element.style.color = `${primary_hex}`
        }
    }, [props.dataItem])

    function displayScheduleInfo() {
        return (
            <div>
                <div className="flex justify-between text-xs">
                    <strong className="break-all capitalize">
                        {schedule_info?.days}
                    </strong>
                </div>
                <div className="flex justify-between text-xs">
                    <strong className="truncate">
                        {schedule_info?.start} - {schedule_info?.end}
                    </strong>
                </div>
                <small className="flex items-center pt-2">
                    {schedule_info?.svc_display.join(", ")}
                </small>
                <div className={classNames("flex justify-between text-xs rounded-xl w-12 p-1",
                    schedule_info?.svc_more.length == 0 ? "hidden" : "visible"
                )}
                    style={{ backgroundColor: `${primaryColor || default_primary_color}60` }}
                    ref={icon_ref}
                    onMouseEnter={() => {
                        setShowMore(true)
                    }}
                    onMouseLeave={() => {
                        setShowMore(false)
                    }}
                >
                    <small className="flex items-center text-xs">
                        {schedule_info?.svc_more.length} more
                    </small>
                </div>
                <small className="flex items-center pt-2">
                    <i className="feather-map-pin mr-1" />
                    {props.dataItem?.location?.name}
                </small>
            </div>
        )
    }

    return (
        <Fragment>
            <SchedulerItem
                {...props}
                style={{
                    ...props.style,
                    borderLeftWidth: "4px",
                }}
                className={classNames(
                    props.className || '',
                    'g-view-item rounded bg-green w-32'
                )}
                ref={ref}
                onMouseEnter={() => {
                    setShowPopup(true)
                }}
                onMouseLeave={() => {
                    setShowPopup(false)
                }}
            >
                <SchedulerItemContent>
                    {displayScheduleInfo()}
                    <Popup
                        show={show_popup}
                        anchorAlign={{
                            horizontal: 'center',
                            vertical: 'top',
                        }}
                        popupAlign={{
                            horizontal: 'center',
                            vertical: 'bottom',
                        }}
                        anchor={ref.current && ref.current.element}
                        popupClass={
                            'bg-slate-200 text-black text-xs shadow-lg rounded-md w-32 py-1 px-2'
                        }
                        className="z-[102]"
                    >
                        {displayScheduleInfo()}
                        <div className="grid justify-items-end">
                            <button className={"flex items-center rounded-lg bg-red-100 w-3 h-3 relative right-0 bg-red-100 text-red-500 text-xs"}
                                onClick={() => {
                                    setShowConfirmation(true)
                                }}
                            >
                                <i className="feather-archive" />
                            </button>
                        </div>
                    </Popup>
                    <Popup
                        anchor={icon_ref.current}
                        show={show_more}
                        animate={false}
                        popupAlign={{
                            horizontal: 'left',
                            vertical: 'top',
                        }}
                        popupClass={
                            'bg-black text-white text-xs shadow-lg rounded-md w-36 py-1 px-2'
                        }
                        className="z-[102]"
                    >
                    </Popup>
                    {showConfirmation && (
                        <ConfirmDialogModal
                            prompt_message={getTranslation('delete_availability_prompt', translations)}
                            onCancel={() => {
                                setShowConfirmation(false)
                            }}
                            onConfirm={() => props.onDelete(props.dataItem.id)}
                        />
                    )}
                </SchedulerItemContent>
            </SchedulerItem>
        </Fragment>
    )
}
